import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Trash } from "lucide-react";
import api, { money, formatApiError, fileUrl } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import { TableShell, Th, Td, EmptyRow } from "@/components/admin/DataTable";
import StatusBadge, { ORDER_FLOW } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Orders() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("cash");

  const load = () => api.get(`/orders?search=${search}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [search]);

  const openOrder = async (id) => { const r = await api.get(`/orders/${id}`); setActive(r.data); };
  const removePermanent = async (id) => { try { await api.delete(`/orders/${id}?permanent=true`); toast.success("Order permanently deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const confirmDown = async () => { try { await api.post(`/orders/${active.id}/confirm-downpayment`); toast.success("Down payment confirmed — order approved"); openOrder(active.id); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const setStatus = async (status) => {
    try { await api.put(`/orders/${active.id}/status`, { status }); toast.success("Status updated"); openOrder(active.id); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const recordPayment = async (index, amount) => {
    try {
      await api.post("/payments", { order_id: active.id, installment_index: index, amount, method: payMethod });
      toast.success("Payment recorded");
      openOrder(active.id); load(); setPayAmt("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Orders">
      <TableShell search={search} onSearch={setSearch} searchPlaceholder="Search order no or customer...">
        <thead className="border-b border-border bg-secondary/40"><tr><Th>Order</Th><Th>Customer</Th><Th>Product</Th><Th>Total</Th><Th>Progress</Th><Th>Status</Th><Th className="text-right">Action</Th></tr></thead>
        <tbody className="divide-y divide-border">
          {items.length === 0 ? <EmptyRow colSpan={7} /> : items.map((o) => {
            const paid = (o.schedule || []).filter((x) => x.paid).length;
            return (
              <tr key={o.id} className="hover:bg-secondary/30" data-testid={`order-row-${o.id}`}>
                <Td className="font-mono text-xs">{o.order_no}</Td>
                <Td>{o.customer_name}</Td>
                <Td>{o.product_name}</Td>
                <Td className="font-semibold">{money(o.total_payable)}</Td>
                <Td>{paid}/{o.schedule?.length}</Td>
                <Td><StatusBadge status={o.status} /></Td>
                <Td className="text-right"><div className="flex justify-end gap-1"><Button size="sm" variant="outline" onClick={() => openOrder(o.id)} data-testid={`view-order-${o.id}`}><Eye className="mr-1 h-3.5 w-3.5" />Manage</Button>
                  {can("orders", "delete") && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="destructive" data-testid={`perm-del-order-${o.id}`}><Trash className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="text-destructive">Permanently delete this order?</AlertDialogTitle><AlertDialogDescription>This will permanently remove order {o.order_no} and its recorded payments. The linked request will be reverted to Approved. This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => removePermanent(o.id)} data-testid={`confirm-perm-del-order-${o.id}`}>Permanently Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                </div></Td>
              </tr>
            );
          })}
        </tbody>
      </TableShell>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3">{active.order_no}<StatusBadge status={active.status} /></DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-4 text-sm">
                  <div><p className="text-muted-foreground">Customer</p><p className="font-medium">{active.customer_name}</p></div>
                  <div><p className="text-muted-foreground">Product</p><p className="font-medium">{active.product_name}</p></div>
                  <div><p className="text-muted-foreground">Order Date</p><p className="font-medium">{active.created_at ? new Date(active.created_at).toLocaleString() : "—"}</p></div>
                  <div><p className="text-muted-foreground">Advance</p><p className="font-medium">{money(active.advance_payment)}</p></div>
                  <div><p className="text-muted-foreground">Total Payable</p><p className="font-medium">{money(active.total_payable)}</p></div>
                  <div><p className="text-muted-foreground">Monthly</p><p className="font-medium text-primary">{money(active.monthly_installment)}</p></div>
                </div>

                {active.status === "Pending Down Payment" && can("orders", "edit") && (
                  <div className="rounded-lg border border-amber-500/40 p-4" data-testid="admin-downpayment">
                    <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Down payment pending — {money(active.advance_payment)}</p>
                    {(active.receipts || []).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">{active.receipts.map((rc, i) => <a key={i} href={fileUrl(rc.file_id)} target="_blank" rel="noreferrer" data-testid={`view-receipt-${i}`} className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-secondary">View Receipt {i + 1}</a>)}</div>
                    ) : <p className="mt-1 text-xs text-muted-foreground">Customer has not uploaded a receipt yet.</p>}
                    <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={confirmDown} data-testid="confirm-downpayment">Confirm Down Payment & Approve</Button>
                  </div>
                )}

                {can("orders", "edit") && (
                  <div><Label className="mb-1.5 block text-xs">Update Tracking Status</Label>
                    <Select value={active.status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ORDER_FLOW.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-display font-semibold">Installment Schedule</h4>
                    {can("payments", "create") && (
                      <Select value={payMethod} onValueChange={setPayMethod}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid="pay-method"><SelectValue /></SelectTrigger>
                        <SelectContent>{["cash", "easypaisa", "jazzcash", "bank", "card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="grid gap-2">
                    {(active.schedule || []).map((s) => (
                      <div key={s.index} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 text-sm">
                        <span className="font-medium">#{s.index}</span>
                        <span className="text-muted-foreground">Due {s.due_date}</span>
                        <span className="ml-auto font-semibold">{money(s.amount)}</span>
                        {s.paid ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">Paid</span> :
                          can("payments", "create") ? <Button size="sm" onClick={() => recordPayment(s.index, s.amount)} data-testid={`record-pay-${s.index}`}>Record</Button> :
                          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400">Pending</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
